import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import '../features/features-help-guide.css';

const img = (name) => (process.env.PUBLIC_URL || '') + '/help-guide/' + name;

const GUIDE_STEPS = [
  {
    key: 'location',
    tone: 'teal',
    image: 'marketplace-help-location.png',
    titleDefault: 'Start from the preferred location',
    bodyDefault:
      'The Location dropdown opens on the location saved as this user’s preferred location. Change it to work on another kiosk. Bins, refill orders, and the shop belong to that location only.',
  },
  {
    key: 'reserve',
    tone: 'orange',
    image: 'marketplace-help-reserve.png',
    titleDefault: 'Reserve one product in one bin',
    bodyDefault:
      'Choose an open bin and an active Marketplace product. The same product can be reserved in more than one bin. A bin cannot be used for a prescription and a Marketplace item at the same time. Maximum must be greater than minimum. Balance on hand cannot be above the maximum. Products already in a bin are marked in the product list.',
  },
  {
    key: 'bins',
    tone: 'purple',
    image: 'marketplace-help-locked-bins.png',
    titleDefault: 'Bins already in use are hidden',
    bodyDefault:
      'The Bin list shows every Shop bin on this kiosk, including bins that already hold a product, plus other bins that are free for your group. A bin is left out when it holds a prescription or retail order (assigned, stocked, on hold, or deactivated), when it is disabled, or when Bin Mapping assigns that column to another group. When Shop is licensed, a reserved bin shows as Shop on Deliver Prescription and on kiosk Maintenance. Releasing a Shop bin with a zero balance puts it back for prescriptions.',
  },
  {
    key: 'levels',
    tone: 'green',
    image: 'marketplace-help-levels.png',
    titleDefault: 'Minimum, Maximum, and balance on hand',
    bodyDefault:
      'Balance on hand is the unit count staff believe is in the bin. The door sensor only knows whether the bin is empty or not; it does not count units. A refill is created when the balance drops below Minimum, and also when the balance is zero and Maximum is greater than zero. The suggested load is Maximum minus the balance on hand. A short load still completes the order. If the bin is still below Minimum afterward, a new refill is created on the next check.',
  },
  {
    key: 'refill',
    tone: 'coral',
    image: 'marketplace-help-load.png',
    titleDefault: 'Load the refill at the kiosk',
    bodyDefault:
      'Each bin that needs stock gets its own load code and its own Connect task. At the kiosk, open Load Marketplace, scan that bin’s code, confirm the quantity, and save. Save opens the bin and marks that order complete. Leaving the load screen before save puts the order back to open.',
  },
  {
    key: 'shop',
    tone: 'teal',
    image: 'marketplace-help-shop.png',
    titleDefault: 'Shoppers pay, then the door opens',
    bodyDefault:
      'A licensed kiosk shows Shop when at least one reserved bin can be sold. The shopper adds items, pays on the payment terminal, and the door for each item opens. There is no patient record. A bin on variance hold, paused, or out of stock is not sold. Units are held while payment is pending and released if payment is not finished.',
  },
  {
    key: 'actions',
    tone: 'orange',
    image: 'marketplace-help-actions.png',
    titleDefault: 'Adjust, unload, or release',
    bodyDefault:
      'Use the actions menu on a reserved bin. Adjust sets a new balance after a count. Unload sets the balance to zero and asks for a reason, such as expired or damaged product. Release removes the reservation so the bin can be used for prescriptions again. Release is allowed only when the balance on hand is zero. Cancel an open refill from this page if it should not be loaded. A refill already being loaded at the kiosk stays locked until that load is saved or left.',
  },
];

const RULES = [
  {
    id: 'license',
    titleDefault: 'Both licenses are required',
    detailDefault:
      'Shop is an add-on. The account also needs Product Marketplace, which holds the product catalog, price, and tax. If either license is off, this page and the kiosk Shop stay unavailable.',
  },
  {
    id: 'one',
    titleDefault: 'Same product, more than one bin',
    detailDefault:
      'The same product can be reserved in more than one bin. A bin still holds one product. Saving that bin again updates the product, balance, minimum, and maximum.',
  },
  {
    id: 'excluded',
    titleDefault: 'In-use bins stay blocked',
    detailDefault:
      'A reserved Marketplace bin shows as Shop on Deliver Prescription and on kiosk Maintenance when that license is on, and it stays blocked from prescriptions and other orders even when the balance on hand is zero. It is not the Locked status. Bin Mapping still applies: you can reserve a bin only in a column mapped to your group, or in a column with no mapping. It returns to normal use only after it is released.',
  },
  {
    id: 'occupancy',
    titleDefault: 'Sensor versus balance',
    detailDefault:
      'Occupied with a balance above zero is fine. Empty with a balance above zero, or occupied with a balance at zero, is a variance. Sales stop until staff adjust the balance or unload the bin. Empty with a balance at zero is out of stock and will refill when Maximum is above zero.',
  },
  {
    id: 'payment',
    titleDefault: 'Payment holds stock',
    detailDefault:
      'Sellable quantity is the balance on hand minus units held for a payment in progress. A hold older than 15 minutes is released the next time someone starts a sale.',
  },
  {
    id: 'door',
    titleDefault: 'A door that does not open',
    detailDefault:
      'If a paid bin does not open, the sale is marked as a door exception and Connect gets a task to open that bin. Staff should not take payment again for that sale.',
  },
];

export const MarketplaceBinsHelpGuide = ({ t, open, onClose }) => {
  const [tab, setTab] = useState('guide');
  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      className="features-help-dialog"
      style={{ zIndex: 1500 }}
      PaperProps={{ className: 'features-help-paper' }}
    >
      <div className="features-help-hero">
        <IconButton
          aria-label={t('MarketplaceBins.Close', { defaultValue: 'Close' })}
          onClick={onClose}
          className="features-help-close"
        >
          <CloseIcon />
        </IconButton>
        <p className="features-help-eyebrow">
          {t('MarketplaceBinsHelp.Eyebrow', { defaultValue: 'Shop' })}
        </p>
        <h2>{t('MarketplaceBinsHelp.Title', { defaultValue: 'Shop guide' })}</h2>
        <p>
          {t('MarketplaceBinsHelp.Subtitle', {
            defaultValue:
              'How to reserve bins, refill them, and sell Marketplace products from the kiosk.',
          })}
        </p>
      </div>
      <Tabs
        value={tab}
        onChange={(event, next) => setTab(next)}
        className="marketplace-bins-help-tabs"
        textColor="inherit"
        indicatorColor="primary"
      >
        <Tab value="guide" label={t('MarketplaceBinsHelp.GuideTab', { defaultValue: 'Guide' })} />
        <Tab value="rules" label={t('MarketplaceBinsHelp.RulesTab', { defaultValue: 'Rules' })} />
      </Tabs>
      <DialogContent className="features-help-body">
        {tab === 'guide' ? (
          <React.Fragment>
            {GUIDE_STEPS.map((step, index) => (
              <section
                key={step.key}
                className={'features-help-step marketplace-bins-help-step is-' + step.tone}
              >
                <div className="features-help-step-copy">
                  <span className="features-help-step-num">{index + 1}</span>
                  <h3>{t('MarketplaceBinsHelp.Step' + (index + 1) + 'Title', { defaultValue: step.titleDefault })}</h3>
                  <p>{t('MarketplaceBinsHelp.Step' + (index + 1) + 'Body', { defaultValue: step.bodyDefault })}</p>
                </div>
                {step.image ? (
                  <div className="features-help-step-media">
                    <img
                      src={img(step.image)}
                      alt={t('MarketplaceBinsHelp.Step' + (index + 1) + 'Title', { defaultValue: step.titleDefault })}
                    />
                  </div>
                ) : null}
              </section>
            ))}
          </React.Fragment>
        ) : (
          <React.Fragment>
            {RULES.map((rule) => (
              <article key={rule.id} className="marketplace-bins-help-rule">
                <h3>{t('MarketplaceBinsHelp.Rule.' + rule.id + '.Title', { defaultValue: rule.titleDefault })}</h3>
                <p>{t('MarketplaceBinsHelp.Rule.' + rule.id + '.Detail', { defaultValue: rule.detailDefault })}</p>
              </article>
            ))}
          </React.Fragment>
        )}
      </DialogContent>
      <DialogActions className="features-help-actions">
        {tab === 'guide' ? (
          <Button onClick={() => setTab('rules')} sx={{ textTransform: 'none', marginRight: 'auto' }}>
            {t('MarketplaceBinsHelp.OpenRules', { defaultValue: 'View rules' })}
          </Button>
        ) : (
          <Button onClick={() => setTab('guide')} sx={{ textTransform: 'none', marginRight: 'auto' }}>
            {t('MarketplaceBinsHelp.OpenGuideTab', { defaultValue: 'Back to the guide' })}
          </Button>
        )}
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('MarketplaceBins.Close', { defaultValue: 'Close' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const MarketplaceBinsHelpButton = ({ t, onOpen }) => (
  <Button
    variant="outlined"
    size="small"
    startIcon={<HelpOutlineIcon />}
    onClick={onOpen}
    sx={{
      textTransform: 'none',
      fontWeight: 600,
      borderColor: '#7dd3fc',
      color: '#0369a1',
      background: '#fff',
      '&:hover': {
        borderColor: '#00a7e1',
        background: 'rgba(0, 167, 225, 0.06)',
      },
    }}
  >
    {t('MarketplaceBinsHelp.Open', { defaultValue: 'Help guide' })}
  </Button>
);

export default MarketplaceBinsHelpGuide;



// WEBPACK FOOTER //
// ./src/components/marketplace-bins/MarketplaceBinsHelpGuide.js